<template>
  <div>
    <div
      class="filters"
      :class="[
        'filters',
        mobileFilterOpened ? 'filters__fullscreen' : 'filters__hide',
      ]"
    >
      <div v-if="mobileFilterOpened" class="filters__mobile-top-bar">
        <div @click="filterBubblePressed()">
          <FilterBackArrowIcon class="filters__mobile-top-bar-back" />
          <span class="filters__mobile-top-bar-title">Filter results</span>
        </div>
        <button class="filters__mobile-top-bar-clear" @click="clearFilters()">
          Clear filters
        </button>
      </div>
      <div class="filters__content">
        <div v-if="!mobileFilterOpened" class="filters__title">
          filter results
        </div>
        <template
          v-if="
            currentContext === 'Courses' || currentContext === 'Learning Paths'
          "
        >
          <div
            :class="[
              'filters__facet',
              hiddenFacets.includes('features')
                ? 'filters__facet--collapsed'
                : 'filters__facet--expanded',
            ]"
            @click="updateHiddenFacets('features')"
          >
            <p class="filters__facet-title">
              Features
              <PlusIcon v-if="hiddenFacets.includes(features)" />
              <MinusIcon v-else />
            </p>
          </div>
          <div
            v-for="feature in featureFilters"
            v-show="!hiddenFacets.includes('features')"
            :key="`filter-wrapper-${feature.name}`"
            class="filters__results-wrapper"
          >
            <template
              v-for="[title, count] in handleResults(
                feature.name,
                feature.data
              )"
            >
              <div
                v-if="title !== 'false'"
                :key="`filter-result-${title}-${count}`"
                :class="[
                  'filters__result',
                  count === 0 && 'filters__result--disabled',
                ]"
              >
                <div
                  :class="[
                    'filters__item',
                    count === 0 && 'filters__item--disabled',
                  ]"
                  @click="
                    updateFilters({ facet: feature.name, value: title }, count)
                  "
                >
                  <input
                    class="filters__item-checkbox"
                    type="checkbox"
                    :checked="checkedFilters.includes(feature.name)"
                    :disabled="count === 0"
                  />
                  <div
                    v-if="feature.name === 'has_labs'"
                    class="filters__item--lab-icon-wrapper"
                  >
                    <LabFilterIcon />
                  </div>
                  <p
                    :class="[
                      'filters__item-title',
                      count === 0 && 'filters__item-title--disabled',
                    ]"
                  >
                    {{ transformFilterTitle(feature.name) }}
                  </p>
                  <p
                    :class="[
                      'filters__item-count',
                      count === 0 && 'filters__item-count--disabled',
                    ]"
                  >
                    {{ count }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </template>
        <template v-for="facet in nonfeatureFilters">
          <div
            :key="`filter-${facet.name}`"
            :class="[
              'filters__facet',
              hiddenFacets.includes(facet.name)
                ? 'filters__facet--collapsed'
                : 'filters__facet--expanded',
            ]"
            @click="updateHiddenFacets(facet.name)"
          >
            <div class="filters__facet-title">
              <span>
                {{ transformFilterTitle(facet.name) }}
              </span>
              <InfoIcon
                v-if="facetHasTooltip(facet.name)"
                v-tooltip="{
                  content: `${facetHasTooltip(facet.name)}`,
                  placement: 'right-center',
                }"
                class="filters__facet-title-icon"
              />
            </div>
            <PlusIcon v-if="hiddenFacets.includes(facet.name)" />
            <MinusIcon v-else />
          </div>
          <div
            v-show="!hiddenFacets.includes(facet.name)"
            :key="`filter-wrapper-${facet.name}`"
            class="filters__results-wrapper"
          >
            <div
              v-for="[title, count] in handleResults(facet.name, facet.data)"
              :key="`filter-result-${title}-${count}`"
              :class="[
                'filters__result',
                count === 0 && 'filters__result--disabled',
              ]"
            >
              <div
                :class="[
                  'filters__item',
                  count === 0 && 'filters__item--disabled',
                ]"
                @click="
                  updateFilters({ facet: facet.name, value: title }, count)
                "
              >
                <input
                  class="filters__item-checkbox"
                  type="checkbox"
                  :checked="checkedFilters.includes(title)"
                  :disabled="count === 0"
                />
                <p
                  :class="[
                    'filters__item-title',
                    count === 0 && 'filters__item-title--disabled',
                  ]"
                >
                  <template v-if="facet.name === 'severity'">
                    <SeverityPill :severity="title" small />
                  </template>
                  <template v-else>
                    {{ title }}
                  </template>
                </p>
                <p class="filters__item-count">{{ count }}</p>
              </div>
            </div>
            <div
              v-if="isButtonShown(facet.data)"
              class="filters__show-more-btn__wrapper"
            >
              <button
                class="filters__show-more-btn"
                @click="updateIsShowingMore(facet.name)"
              >
                {{
                  isShowingMore.includes(facet.name) ? 'show less' : 'show more'
                }}
                <i
                  v-if="shouldDisplayShowMore(facet)"
                  class="material-icons filters__show-more-btn__arrow"
                >
                  keyboard_arrow_down
                </i>
                <i v-else class="material-icons filters__show-more-btn__arrow">
                  keyboard_arrow_up
                </i>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="filters__bubble-buttom-container">
      <button
        v-if="mobileFilterOpened"
        class="filters__results-button"
        @click="filterBubblePressed()"
      >
        {{ `See ${totalResults ? totalResults : ''} results` }}
      </button>
      <button
        v-else
        class="filters__open-bubble"
        @click="filterBubblePressed()"
      >
        <div v-if="checkedFilters.length" class="filters__activated-dot"></div>
        <FilterBubbleIcon class="filters__open-bubble--icon"></FilterBubbleIcon>
      </button>
    </div>
  </div>
</template>

<script>
import LabFilterIcon from '@/assets/icons/search/lab_filter.svg'
import FilterBubbleIcon from '@/assets/icons/search/filter_bubble.svg'
import FilterBackArrowIcon from '@/assets/icons/search/filter_back_arrow.svg'
import CONSTANTS from '@/constants'
import { PlusIcon, MinusIcon, InfoIcon } from 'vue-feather-icons'
import SeverityPill from '@/components/properties/SeverityPill.vue'

export default {
  name: 'Filters',
  components: {
    LabFilterIcon: { ...LabFilterIcon, name: 'LabFilterIcon' },
    FilterBubbleIcon: { ...FilterBubbleIcon, name: 'FilterBubbleIcon' },
    FilterBackArrowIcon: {
      ...FilterBackArrowIcon,
      name: 'FilterBackArrowIcon',
    },
    PlusIcon,
    MinusIcon,
    InfoIcon,
    SeverityPill,
  },
  props: {
    currentContext: {
      type: String,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
    filters: {
      type: [Array, Object],
      required: true,
    },
    currentRefinedValues: {
      type: Array,
      required: true,
    },
    filterMap: {
      type: Object,
      required: true,
    },
    filterOrders: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    totalResults: {
      type: Number,
      required: false,
      default: () => 0,
    },
    avoidCompleting: {
      type: Boolean,
      required: false,
      default: false,
    },
    startCollapsed: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isShowingMore: ['features'],
      hiddenFacets: [],
      mobileFilterOpened: false,
      firstCollapseAll: true,
    }
  },
  computed: {
    featureFilters() {
      const featureFilters = []
      const features = this.isFreeTrialUser
        ? this.features
        : this.features.filter(f => f !== 'has_sample_content')
      if (this.filters && this.filters.length) {
        this.filters.map(filter => {
          if (features.includes(filter.name)) {
            if (!filter.data.true) filter.data.true = 0
            featureFilters.push(filter)
          }
        })
      }
      return featureFilters
    },
    nonfeatureFilters() {
      const nonfeatureFilters = []
      if (this.filters && this.filters.length) {
        this.filters.map(filter => {
          if (!this.features.includes(filter.name)) {
            nonfeatureFilters.push(filter)
          }
        })
      }
      return this.fillNonFeatureFilters(nonfeatureFilters)
    },
    checkedFilters() {
      return this.currentRefinedValues.flat(2)
    },
  },
  beforeUpdate() {
    if (this.startCollapsed && this.firstCollapseAll) {
      this.nonfeatureFilters.forEach(facet => {
        this.updateHiddenFacets(facet.name)
      })
      this.features.forEach(facet => {
        this.updateHiddenFacets(facet.name)
      })
      this.firstCollapseAll =
        this.firstCollapseAll && !this.hiddenFacets.length
          ? this.firstCollapseAll
          : false
    }
  },
  methods: {
    fillNonFeatureFilters(filters) {
      return filters.map(filter => {
        if (filter.name !== 'learning_areas.name' || this.avoidCompleting)
          return filter
        return this.fillNonFeatureFilter(filter, {
          ...CONSTANTS.LEARNING_AREAS.PRIMARY,
          ...CONSTANTS.LEARNING_AREAS.SECONDARY,
        })
      })
    },
    fillNonFeatureFilter(filter, data) {
      Object.keys(data).map(filterName => {
        if (!Object.prototype.hasOwnProperty.call(filter.data, filterName)) {
          filter.data[filterName] = 0
        }
      })
      return filter
    },
    shouldDisplayShowMore(facet) {
      return !this.isShowingMore.includes(facet.name)
    },
    transformFilterTitle(filter) {
      return this.filterMap[filter] || filter
    },
    updateHiddenFacets(facet) {
      if (this.hiddenFacets.includes(facet)) {
        this.hiddenFacets = this.hiddenFacets.filter(f => f !== facet)
      } else {
        this.hiddenFacets.push(facet)
      }
    },
    handleResults(facet, results) {
      if (!results) return []

      const showQuantity = this.isShowingMore.includes(facet) ? 15 : 5

      if (this.filterOrders[facet]) {
        results = this.filterOrders[facet].map(title => [title, results[title]])
      } else {
        results = Object.entries(results)
      }

      return results.slice(0, showQuantity)
    },
    isButtonShown(results) {
      return Object.keys(results).length > 5
    },
    updateIsShowingMore(facet) {
      if (this.isShowingMore.includes(facet)) {
        this.isShowingMore = this.isShowingMore.filter(f => f !== facet)
      } else {
        this.isShowingMore.push(facet)
      }
    },
    updateFilters({ facet, value }, count) {
      if (!count) return
      this.$emit('update-filters', { facet, value })
    },
    filterBubblePressed() {
      window.scrollTo(0, 0)
      this.mobileFilterOpened = !this.mobileFilterOpened
    },
    clearFilters() {
      this.$emit('clear-filter')
    },
    facetHasTooltip(facetName) {
      let tooltipInfo = false
      switch (facetName) {
        case 'severity':
          tooltipInfo = 'Applies exclusively<br/> to Vulnerability labs'
          break
        default:
          tooltipInfo = false
          break
      }
      return tooltipInfo
    },
  },
}
</script>

<style lang="scss" scoped>
.filters {
  $this: &;
  &__mobile-top-bar {
    position: fixed;
    height: 40px;
    width: 100%;
    background-color: var(--darker-gray);
    z-index: 1;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &-title {
      margin-left: 20px;
      text-transform: capitalize;
      color: var(--white);
      font-size: 20px;
      font-family: var(--font-bold);
    }
    &-back {
      color: var(--white);
      margin-left: 14px;
    }
    &-clear {
      border: none;
      background: none;
      color: var(--white);
      margin-left: 14px;
      font-size: 14px;
      font-family: var(--font-book);
      padding: 0;
      margin-right: 16px;
    }
  }
  &__content {
    margin-top: 55px;
    @include mobile {
      width: 100%;
      margin-top: 0;
    }
  }
  &__hide {
    display: none;
  }
  &__fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    background: var(--dark-brighter);
    z-index: 202;
    width: 100vw;
    height: 100vh;
    overflow: scroll;
    padding: 15px;
  }
  @include mobile {
    overflow: none;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    width: auto;
    height: auto;
    padding: auto;
  }
  &__bubble-buttom-container {
    @include mobile {
      display: none;
    }
  }
  &__activated-dot {
    width: 13px;
    height: 13px;
    background: var(--oranger-gradient);
    position: fixed;
    z-index: 9999;
    bottom: 40px;
    right: 11px;
    border-radius: 10px;
  }
  &__results-button {
    border: none;
    width: 93%;
    height: 40px;
    border-radius: 8px;
    background: var(--white);
    z-index: 203;
    position: fixed;
    bottom: 10px;
    left: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__open-bubble {
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--white);
    z-index: 203;
    position: fixed;
    bottom: 10px;
    right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    &--icon {
      width: 18px;
      height: 17px;
    }
  }

  &__title {
    color: var(--white);
    width: 100%;
    margin: 0 0 30px 0;
    font-size: 1.25rem;
    font-weight: bold;
    text-transform: capitalize;
  }
  &__facet {
    margin: 30px 0 10px 0;
    display: flex;
    align-items: center;
    padding: 5px 0;
    width: 100%;
    border-radius: 10px;
    @include tablet {
      padding: 15px 0;
    }
    &--collapsed {
      background: none;
      &:hover {
        background: var(--glass-mid-gray);
        cursor: pointer;
      }
      &:active {
        background: none;
        outline: 1px solid var(--flat-black) !important;
      }
    }
    &--expanded {
      background: var(--glass-mid-gray);
      &:hover {
        background: var(--glass-mid-gray-light);
        cursor: pointer;
      }
      &:active {
        background: none;
        outline: 1px solid var(--flat-black) !important;
      }
    }
  }
  &__facet-title {
    text-transform: capitalize;
    color: var(--white);
    transition: 0.5s all ease;
    font-family: var(--font-book);
    font-size: 1.1rem;
    margin: 0 0 0 15px;
    flex: 1;

    display: flex;
    align-items: center;
    gap: 10px;

    &-icon {
      height: 14px;
      width: 14px;
    }
  }
  &__results-wrapper {
    border-radius: 10px;
    margin: 0;
    padding: 0 0 0 5px;
    animation: fadeIn 1s ease-out;
    width: 100%;
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }
  &__result {
    width: 100%;
    border-bottom: 1px solid #323232;
    color: var(--white);
    text-transform: capitalize;
    transition: 0.5s all ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    &:hover {
      cursor: pointer;
      color: var(--orange);
    }
    @include tablet {
      padding: 3px;
    }
    &--toggle {
      border: none;
      padding: 0;
    }
  }
  &__item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 0;
    cursor: pointer;
    &--disabled {
      cursor: initial;
    }
    &-checkbox {
      border: 1px solid var(--mid-gray-dark);
      border-radius: 8px;
      margin: 0 5px;
      appearance: none;
      background: none;
      color: var(--white);
      padding: 11px;
      &:hover:not([disabled]) {
        border: 1px solid var(--white);
        cursor: pointer;
      }
      &:checked {
        border: 1px solid var(--white);
        background: var(--mid-gray-dark-25);
        color: var(--white);
        position: relative;
        display: inline-block;
        @include tablet {
          width: 22px;
          height: 22px;
        }
        &::before {
          transform: rotate(40deg);
          content: '';
          position: absolute;
          width: 1px;
          height: 9px;
          background-color: var(--white);
          left: 12px;
          top: 6px;
        }
        &::after {
          transform: rotate(-45deg);
          content: '';
          position: absolute;
          width: 1px;
          height: 4px;
          background-color: var(--white);
          left: 8px;
          top: 10px;
        }
      }
    }
    &--lab-icon-wrapper {
      width: 24px;
      height: 24px;
      background: var(--purple-gradient);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      margin: 0 0 0 10px;
    }
    &-title {
      font-size: 1rem;
      margin: 0 0 0 10px;
      color: var(--white);
      padding: 0;
      &--disabled {
        color: gray;
      }
    }
    &-count {
      font-size: 1rem;
      margin: 0 0 0 auto;
      border-radius: 25px;
      color: var(--white);
      padding: 0;
      &--disabled {
        color: gray;
      }
    }
  }
  &__show-more-btn {
    &__wrapper {
      margin: 15px auto 30px auto;
      width: auto;
      display: flex;
      justify-content: center;
    }
    display: flex;
    align-items: center;
    color: var(--white);
    border: none;
    background: var(--menu-btn);
    border-radius: 25px;
    cursor: pointer;
    justify-content: center;
    text-transform: capitalize;
    font-size: 0.8rem;
    padding: 5px 7px 5px 15px;
    &:hover {
      border: 1px solid var(--dove-gray);
      padding: 4px 6px 4px 14px;
      background: var(--emperor-gray);
      box-shadow: var(--white-medium-box-shadow);
    }
    &:active {
      background: var(--darker-gray);
    }
    &__arrow {
      margin: 0 5px 0 10px;
    }
  }
}
</style>
